/**
 * HeatmapLegend Component
 * 
 * Visual gradient bar showing intensity scale with labels and color scheme selector.
 * Displays the color mapping from 0% (no activity) to 100% (high activity).
 * 
 * Features:
 * - Gradient bar with percentage labels
 * - Color scheme selector (Cold-Hot, Green-Red, Grayscale)
 * - Statistics display (max/min/average intensity)
 * - Compact and responsive design
 * 
 * @author BizClick WMS
 * @version 1.28 - Fase 6 Implementation
 */

import React from 'react';
import { Droplets, TrendingUp, Activity } from 'lucide-react';

interface HeatmapLegendProps {
  /** Current color scheme */
  colorScheme: 'cold-hot' | 'green-red' | 'grayscale';
  
  /** Callback when color scheme changes */
  onColorSchemeChange: (scheme: 'cold-hot' | 'green-red' | 'grayscale') => void;
  
  /** Optional: Maximum intensity in current data */
  maxIntensity?: number;
  
  /** Optional: Minimum intensity in current data */
  minIntensity?: number;
  
  /** Optional: Average intensity in current data */
  averageIntensity?: number;
  
  /** Optional: Total events analyzed */
  totalEvents?: number;
}

/**
 * Gradient styles for each color scheme
 */
const GRADIENT_STYLES: Record<'cold-hot' | 'green-red' | 'grayscale', string> = {
  'cold-hot': 'bg-gradient-to-r from-blue-100 via-yellow-300 via-orange-400 to-red-500',
  'green-red': 'bg-gradient-to-r from-green-200 via-yellow-300 via-orange-400 to-red-500',
  'grayscale': 'bg-gradient-to-r from-gray-200 via-gray-400 via-gray-600 to-gray-800'
};

/**
 * Color scheme names (Norwegian)
 */
const COLOR_SCHEME_NAMES: Record<'cold-hot' | 'green-red' | 'grayscale', string> = {
  'cold-hot': 'Kald-Varm',
  'green-red': 'Grønn-Rød',
  'grayscale': 'Gråskala'
};

/**
 * Color scheme icons
 */
const COLOR_SCHEME_ICONS: Record<'cold-hot' | 'green-red' | 'grayscale', React.ReactNode> = {
  'cold-hot': <Droplets className="w-4 h-4" />,
  'green-red': <TrendingUp className="w-4 h-4" />,
  'grayscale': <Activity className="w-4 h-4" />
};

export const HeatmapLegend: React.FC<HeatmapLegendProps> = ({
  colorScheme,
  onColorSchemeChange,
  maxIntensity,
  minIntensity,
  averageIntensity,
  totalEvents
}) => {
  const gradientClass = GRADIENT_STYLES[colorScheme];

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">Intensitetsskala</h3>
        
        {/* Color scheme selector */}
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
          {(['cold-hot', 'green-red', 'grayscale'] as const).map((scheme) => (
            <button
              key={scheme}
              onClick={() => onColorSchemeChange(scheme)}
              className={`
                flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                transition-colors
                ${colorScheme === scheme
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
              title={COLOR_SCHEME_NAMES[scheme]}
            >
              {COLOR_SCHEME_ICONS[scheme]}
              <span className="hidden sm:inline">{COLOR_SCHEME_NAMES[scheme]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Gradient bar with labels */}
      <div className="space-y-2">
        {/* Gradient */}
        <div 
          className={`h-8 rounded-lg ${gradientClass} border border-gray-300 shadow-inner`}
          style={{ width: '100%' }}
        />
        
        {/* Labels */}
        <div className="flex justify-between items-center text-xs">
          <div className="flex flex-col items-start">
            <span className="font-semibold text-gray-700">0%</span>
            <span className="text-gray-500">Ingen aktivitet</span>
          </div>
          
          <div className="flex flex-col items-center">
            <span className="font-semibold text-gray-700">50%</span>
            <span className="text-gray-500">Moderat</span>
          </div>
          
          <div className="flex flex-col items-end">
            <span className="font-semibold text-gray-700">100%</span>
            <span className="text-gray-500">Høy aktivitet</span>
          </div>
        </div>
      </div>

      {/* Statistics */}
      {(maxIntensity !== undefined || minIntensity !== undefined || averageIntensity !== undefined) && (
        <div className="pt-3 border-t border-gray-200 space-y-1.5">
          <div className="text-xs font-semibold text-gray-600 mb-2">STATISTIKK:</div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            {maxIntensity !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Maks:</span>
                <span className="font-semibold text-red-600">{maxIntensity.toFixed(1)}%</span>
              </div>
            )}
            
            {minIntensity !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Min:</span>
                <span className="font-semibold text-blue-600">{minIntensity.toFixed(1)}%</span>
              </div>
            )}
            
            {averageIntensity !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Gjennomsnitt:</span>
                <span className="font-semibold text-orange-600">{averageIntensity.toFixed(1)}%</span>
              </div>
            )}
            
            {totalEvents !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Totalt events:</span>
                <span className="font-semibold text-gray-700">{totalEvents}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info text */}
      <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
        💡 <span className="font-medium">Tips:</span> Hold musen over en lokasjon for detaljert aktivitetsfordeling.
      </div>
    </div>
  );
};

export default HeatmapLegend;
